import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Sentry } from '../lib/sentry';
import {
  cacheSubscription,
  getCachedSubscription,
  clearCachedSubscription,
} from '../lib/sub-cache';
import type { User, AuthError } from '@supabase/supabase-js';
import type { Profile, Subscription } from '../types/auth';
import { logBetaEvent, logBetaIssue } from '../lib/beta-ops';
import {
  clearClientFamilyData,
  getLastActiveUserId,
  setLastActiveUserId,
} from '../lib/client-data';
import { useSettingsStore } from './settingsStore';
import { isHashedPin, type HashedPin } from '../utils/pin-crypto';
import { trackEmailVerified } from '../lib/analytics';

/**
 * Funnel: record `email_verified` exactly once per user, and only when the
 * verification is FRESH (confirmed within the last hour). The freshness
 * window keeps long-standing accounts from emitting stale events on every
 * new device; the per-user localStorage key dedupes the verify-page poll
 * racing the SIGNED_IN auth event on the same device.
 */
function trackFreshEmailVerification(user: User | null, authToken: string | undefined): void {
  if (!user?.email_confirmed_at) return;
  try {
    const confirmedAt = Date.parse(user.email_confirmed_at);
    if (!Number.isFinite(confirmedAt) || Date.now() - confirmedAt > 60 * 60 * 1000) return;
    const key = `hsy-email-verified-tracked:${user.id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    if (authToken) trackEmailVerified(authToken);
  } catch { /* storage unavailable — skip rather than double-count */ }
}

/**
 * Cross-device parental consent. Consent is recorded into auth user_metadata
 * at signup (synced to every device by Supabase), so a parent who signs up on
 * their phone and later opens the verify link on a laptop isn't re-prompted.
 * Hydrates the local settingsStore only when it has no consent yet — never
 * overwrites a fresher local record.
 */
function hydrateConsentFromMetadata(user: User | null): void {
  try {
    if (!user) return;
    const md = (user.user_metadata ?? {}) as Record<string, unknown>;
    const acceptedAt = md.consent_accepted_at;
    const policyVersion = md.consent_policy_version;
    if (typeof acceptedAt !== 'string' || typeof policyVersion !== 'string') return;
    const settings = useSettingsStore.getState();
    if (settings.parentalConsent) return;
    const name = md.full_name;
    settings.recordParentalConsent({
      acceptedAt,
      parentLegalName: typeof name === 'string' && name !== 'Parent' ? name : 'Parent',
      policyVersion,
    });
  } catch {
    // Never block auth on consent hydration.
  }
}

/**
 * Cross-device parent PIN. The PBKDF2 hash is mirrored into auth user_metadata
 * by syncParentPinToAccount whenever a PIN is set (like consent). Here we only
 * ADOPT it onto a device that has no PIN of its own — so signing in on a new
 * device verifies the account's existing PIN instead of forcing a fresh one.
 *
 * Adopt-only on purpose: we never push a device's local PIN up here, because
 * the local PIN isn't cleared on sign-out, so on a shared browser it could
 * belong to a previously-signed-in account — pushing it would leak one
 * parent's PIN onto another's account. And we never overwrite an existing
 * local PIN. Net effect: new PINs sync forward; nothing existing is disturbed.
 */
function syncParentPinWithMetadata(user: User | null): void {
  try {
    if (!user) return;
    const md = (user.user_metadata ?? {}) as Record<string, unknown>;
    const serverPin = md.parent_pin;
    const settings = useSettingsStore.getState();
    if (isHashedPin(serverPin) && !settings.parentPin) {
      // New device with no PIN yet: adopt the account's → verify mode, not setup.
      settings.setParentPin(serverPin);
    }
  } catch {
    // Never block auth on PIN hydration.
  }
}

/**
 * Canonical app origin for OAuth + email-link redirects.
 *
 * Why hardcode? `window.location.origin` lets any host that loads the
 * bundle nominate itself as the redirect target. Supabase's allowlist is
 * the real defense, but this is one extra layer: if a Vercel preview, a
 * subdomain takeover, or a stray MITM ever serves our JS, the redirect
 * still goes to production. The constant is read from a build-time env
 * var so staging/preview deploys can override it intentionally.
 */
const APP_ORIGIN: string =
  (import.meta.env.VITE_APP_ORIGIN as string | undefined) ??
  (import.meta.env.PROD ? '' : window.location.origin);

function appUrl(path: string): string {
  if (!APP_ORIGIN) {
    // Production build with no VITE_APP_ORIGIN set — fail loud rather
    // than silently fall back to a tamperable origin.
    throw new Error('VITE_APP_ORIGIN is not set; set it at build time.');
  }
  return `${APP_ORIGIN}${path}`;
}

async function ensureProfileBootstrap(): Promise<void> {
  const { error } = await supabase.rpc('ensure_profile_bootstrap');
  if (error) {
    if (error.code === 'PGRST202') {
      console.warn('[auth] ensure_profile_bootstrap RPC not deployed yet; continuing with legacy trigger flow');
      return;
    }
    throw new Error(`ensure_profile_bootstrap: ${error.message}`);
  }
}

/**
 * Pull remote rows first, then push the merged local roster/completions.
 * Fire-and-forget; logs but never throws. Pull-first is required for durable
 * soft deletion: a stale device must see remote archive markers before it can
 * send its local copy back to the server.
 */
function bootstrapCloudSync(userId: string): void {
  void (async () => {
    try {
      const { pushChildren, pushCompletions, pullAndMerge } = await import('../lib/cloud-sync');
      await pullAndMerge(userId).catch(e =>
        console.warn('[cloud-sync] pull on bootstrap failed:', e),
      );
      await pushChildren(userId).catch(e =>
        console.warn('[cloud-sync] push children on bootstrap failed:', e),
      );
      await pushCompletions(userId).catch(e =>
        console.warn('[cloud-sync] push completions on bootstrap failed:', e),
      );
    } catch (e) {
      console.warn('[cloud-sync] bootstrap failed to import:', e);
    }
  })();
}

async function reconcileActiveUser(userId: string): Promise<boolean> {
  const previousUserId = getLastActiveUserId();
  if (previousUserId && previousUserId !== userId) {
    await clearClientFamilyData();
    setLastActiveUserId(userId);
    logBetaEvent('auth.account_switched', { previousUserId, nextUserId: userId }, 'warning');
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
    return true;
  }

  setLastActiveUserId(userId);
  return false;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  subscription: Subscription | null;
  /** true when subscription was loaded from cache and period has expired */
  offlineExpired: boolean;
  loading: boolean;
  initialized: boolean;

  // Actions
  initialize: () => Promise<void>;
  signUp: (email: string, password: string, displayName?: string, persona?: string, betaCodeId?: string, referralCode?: string, ambassadorCode?: string, inviteCode?: string, utm?: Record<string, string>, consent?: { acceptedAt: string; policyVersion: string }, faithIntent?: boolean, discoverySource?: string) => Promise<{ error: AuthError | null; alreadyRegistered?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  /** Mirror the parent PIN hash into auth user_metadata so it follows the account across devices. */
  syncParentPinToAccount: (pin: HashedPin | null) => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  refreshProfile: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  subscription: null,
  offlineExpired: false,
  loading: true,
  initialized: false,

  initialize: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const reloaded = await reconcileActiveUser(session.user.id);
        if (reloaded) return;

        set({ user: session.user });
        hydrateConsentFromMetadata(session.user);
        syncParentPinWithMetadata(session.user);
        trackFreshEmailVerification(session.user, session.access_token);
        await ensureProfileBootstrap();
        // Run profile and subscription refresh independently —
        // if one fails (offline), the other can still succeed from cache
        await Promise.allSettled([
          get().refreshProfile(),
          get().refreshSubscription(),
        ]);

        // Bootstrap cloud sync on existing session (page reload, returning
        // device). Push local children/completions first so any work done
        // while offline reaches the cloud, then pull so any remote-only
        // records (added on another device) hydrate locally.
        bootstrapCloudSync(session.user.id);
      }
    } catch (e) {
      console.error('[auth] init error:', e);
      Sentry.captureException(e);
      logBetaIssue('auth.initialize', e);
    } finally {
      set({ loading: false, initialized: true });
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user ?? null;
      set({ user });

      if (event === 'SIGNED_IN' && user) {
        const reloaded = await reconcileActiveUser(user.id);
        if (reloaded) return;

        logBetaEvent('auth.signed_in', { provider: 'session', hasUser: true });
        hydrateConsentFromMetadata(user);
        syncParentPinWithMetadata(user);
        trackFreshEmailVerification(user, session?.access_token);
        await ensureProfileBootstrap();
        await get().refreshProfile();
        await get().refreshSubscription();

        bootstrapCloudSync(user.id);
      } else if (event === 'SIGNED_OUT') {
        logBetaEvent('auth.signed_out', { hasUser: false });
        set({ profile: null, subscription: null });
      } else if (event === 'TOKEN_REFRESHED' && user) {
        // Silently refresh profile/subscription
        get().refreshProfile();
        get().refreshSubscription();
      }
    });
  },

  signUp: async (email, password, displayName, persona, betaCodeId, referralCode, ambassadorCode, inviteCode, utm, consent, faithIntent, discoverySource) => {
    set({ loading: true });
    // Default display name to a generic label rather than the email
    // local-part. firstname.lastname@gmail.com → "firstname.lastname"
    // would silently expose real-name PII into any future shared/social
    // surface. The user can edit it from settings later.
    const safeName = (displayName || '').trim().slice(0, 60) || 'Parent';
    // Persona is a Phase 0 attribution signal ('parent' | 'educator' | 'both').
    // Stored in user_metadata so it's queryable via
    //   SELECT raw_user_meta_data->>'persona' FROM auth.users
    // without a schema migration. Validated to an enum server-agnostically.
    const validPersonas = ['parent', 'educator', 'both'] as const;
    const safePersona = validPersonas.includes(persona as typeof validPersonas[number])
      ? persona
      : undefined;
    // Beta-code attribution: when the user came in via a server-validated
    // invite, we record the code id so we can audit which family used which
    // seat. The edge function has already burned the use; this is just for
    // bookkeeping.
    const safeBetaCodeId = typeof betaCodeId === 'string' && /^[0-9a-f-]{36}$/i.test(betaCodeId)
      ? betaCodeId
      : undefined;
    const safeReferralCode = typeof referralCode === 'string' && /^[A-Za-z0-9-]{3,30}$/.test(referralCode)
      ? referralCode.toUpperCase()
      : undefined;
    const safeAmbassadorCode = typeof ambassadorCode === 'string' && /^[A-Za-z0-9-]{3,30}$/.test(ambassadorCode)
      ? ambassadorCode.toUpperCase()
      : undefined;
    // Invite code attribution: when the user arrived via an invite link
    // (?invite=HSY-XXXXXX), the code was already claimed by the
    // validate-invite-code edge function. We store it in user_metadata
    // so the handle_new_user trigger can set profiles.referred_by_invite.
    const safeInviteCode = typeof inviteCode === 'string' && /^HSY-[A-Z0-9]{6}$/.test(inviteCode.toUpperCase())
      ? inviteCode.toUpperCase()
      : undefined;
    // Ad-campaign attribution (first-touch utm_* captured in utils/utm.ts).
    // Stored in user_metadata like persona — queryable via
    //   SELECT raw_user_meta_data->>'utm_campaign' FROM auth.users
    const UTM_META_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref_host', 'landing_path', 'captured_at'];
    const safeUtm: Record<string, string> = {};
    if (utm) {
      for (const key of UTM_META_KEYS) {
        const value = utm[key];
        if (typeof value === 'string' && value) safeUtm[key] = value.slice(0, 100);
      }
    }
    const DISCOVERY_SOURCES = ['ai_assistant', 'search_engine', 'social_media', 'friend_family', 'homeschool_community', 'other'] as const;
    const safeDiscoverySource = DISCOVERY_SOURCES.includes(discoverySource as typeof DISCOVERY_SOURCES[number])
      ? discoverySource
      : undefined;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: safeName,
          ...(safePersona ? { persona: safePersona } : {}),
          ...(safeBetaCodeId ? { beta_code_id: safeBetaCodeId } : {}),
          ...(safeReferralCode ? { referral_code: safeReferralCode } : {}),
          ...(safeAmbassadorCode ? { ambassador_code: safeAmbassadorCode } : {}),
          ...(safeInviteCode ? { invite_code: safeInviteCode } : {}),
          ...safeUtm,
          // Consent recorded into auth metadata so it follows the parent to a
          // second device (e.g. verify on laptop after signing up on phone) —
          // hydrateConsentFromMetadata reads it back, no re-prompt.
          ...(consent?.acceptedAt && consent?.policyVersion
            ? { consent_accepted_at: consent.acceptedAt, consent_policy_version: consent.policyVersion }
            : {}),
          // Faith-content intent from the christian-curriculum landing page.
          // Stored in metadata (like utm/consent) so it survives verifying on
          // a DIFFERENT device — WelcomePage falls back to this when the
          // hsy-faith-intent localStorage flag isn't present.
          ...(faithIntent ? { faith_intent: true } : {}),
          // Optional self-report closes the attribution gap when an AI app or
          // cross-device flow strips the original referrer.
          ...(safeDiscoverySource ? { discovery_source: safeDiscoverySource } : {}),
        },
        emailRedirectTo: appUrl('/'),
      },
    });
    // Supabase email-enumeration protection: signing up an already-registered
    // (confirmed) email returns NO error but an obfuscated user with an EMPTY
    // identities array. Detect it so the UI can route the parent to log in /
    // reset instead of a dead-end "check your email" that never arrives — and
    // so we don't record it as a real new signup (it was inflating the funnel).
    const alreadyRegistered =
      !error && Array.isArray(data.user?.identities) && data.user.identities.length === 0;
    if (error) logBetaIssue('auth.sign_up', error, { method: 'password' });
    else logBetaEvent('auth.sign_up', { method: 'password', hasDisplayName: !!displayName, alreadyRegistered });
    set({ loading: false });
    return { error, alreadyRegistered };
  },

  signIn: async (email, password) => {
    set({ loading: true });
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) logBetaIssue('auth.sign_in', error, { method: 'password' });
    else logBetaEvent('auth.sign_in', { method: 'password' });
    set({ loading: false });
    return { error };
  },

  signInWithGoogle: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: appUrl('/'),
      },
    });
    if (error) logBetaIssue('auth.sign_in_oauth', error, { provider: 'google' });
    else logBetaEvent('auth.sign_in_oauth', { provider: 'google' });
    return { error };
  },

  signOut: async () => {
    // Clear local auth state FIRST, before any network call — a logout button
    // must never no-op. Previously this awaited supabase.auth.signOut() up
    // front with no try/catch; if that server call rejected (expired/invalid
    // session, network blip) the state-clearing line below never ran and the
    // user stayed "logged in".
    clearCachedSubscription();
    Sentry.setUser(null);
    logBetaEvent('auth.sign_out');
    set({ user: null, profile: null, subscription: null, offlineExpired: false });
    try {
      // 'local' scope drops this device's session without a server round-trip,
      // so it can't throw on an already-expired/invalid session.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      logBetaIssue('auth.sign_out', e);
    }
    // Hard navigation guarantees every in-memory store and route resets and
    // auth is re-evaluated from scratch (no session → login screen). Without
    // this, lingering persisted/route state could keep the app looking signed in.
    if (typeof window !== 'undefined') {
      window.location.assign('/login');
    }
  },

  syncParentPinToAccount: async (pin) => {
    // Mirror the hash (never plaintext) into user_metadata so the PIN follows
    // the account to any device. Fire-and-forget — a transient failure just
    // means the next sign-in reconcile (syncParentPinWithMetadata) lifts it up.
    try {
      await supabase.auth.updateUser({ data: { parent_pin: pin } });
    } catch (e) {
      logBetaIssue('auth.sync_parent_pin', e);
    }
  },

  resetPassword: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: appUrl('/reset-password'),
    });
    if (error) logBetaIssue('auth.reset_password', error);
    else logBetaEvent('auth.reset_password');
    return { error };
  },

  refreshProfile: async () => {
    const user = get().user;
    if (!user) return;

    try {
      await ensureProfileBootstrap();
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      if (data) {
        set({ profile: data as Profile });
        return;
      }

      throw new Error('Profile not found after bootstrap');
    } catch (e) {
      Sentry.captureException(e, { tags: { context: 'profile_refresh' } });
      logBetaIssue('auth.refresh_profile', e);
    }
  },

  refreshSubscription: async () => {
    const user = get().user;
    if (!user) return;

    try {
      await ensureProfileBootstrap();
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('profile_id', user.id)
        .in('status', ['trialing', 'active', 'past_due'])
        .single();

      // PGRST116 = "no rows returned" from .single() — this is NOT a network error,
      // it means the user genuinely has no active subscription
      if (error && error.code === 'PGRST116') {
        clearCachedSubscription();
        set({ subscription: null, offlineExpired: false });
        return;
      }
      if (error) throw error;

      if (data) {
        const sub = data as Subscription;
        await cacheSubscription(sub, user.id);
        set({ subscription: sub, offlineExpired: false });
      } else {
        // No active subscription found on server — clear cache
        clearCachedSubscription();
        set({ subscription: null, offlineExpired: false });
      }
    } catch (e) {
      // Network error / offline — fall back to cached subscription
      Sentry.captureException(e, { tags: { context: 'subscription_refresh' } });
      const cached = await getCachedSubscription(user.id);
      if (cached) {
        set({
          subscription: cached.subscription,
          offlineExpired: cached.expired,
        });
      } else {
        set({ subscription: null, offlineExpired: false });
      }
    }
  },
}));
